import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataSourcesService from '../service';
import { yfTranslationKey } from '../yfDataStatusUtils';

// Each in-flight request holds a web worker on the other end.
const MAX_CONCURRENT_CALCULATIONS = 4;

function _withRow(snapshot, row) {
    if (!snapshot) {
        return snapshot;
    }
    return {
        ...snapshot,
        rows: snapshot.rows.map(current => (current.uan_id === row.uan_id ? row : current)),
    };
}

function _withoutKey(errors, key) {
    const { [key]: removed, ...rest } = errors;
    return rest;
}

/** A row button always forces a re-pull; "calculate all" reuses pulls still inside the TTL. */
export default function useYfAuditStatus({ translate, service: injectedService } = {}) {
    // A new instance per render would re-run the load effect forever.
    const service = useMemo(() => injectedService || new DataSourcesService(), [injectedService]);

    const [snapshot, setSnapshot] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [calculationErrors, setCalculationErrors] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [startedAt, setStartedAt] = useState({});
    const [queuedUanIds, setQueuedUanIds] = useState([]);
    const [bulk, setBulk] = useState(null);
    const [showArchived, setShowArchived] = useState(false);

    const stopRequested = useRef(false);

    const visibleRows = useMemo(() => {
        const rows = snapshot ? snapshot.rows : [];
        return showArchived ? rows : rows.filter(row => !row.show_in_archive);
    }, [snapshot, showArchived]);

    const archivedCount = useMemo(() => (snapshot ? snapshot.rows.filter(row => row.show_in_archive).length : 0), [
        snapshot,
    ]);

    // Never archived rows: they spend a rate-limited call to learn nothing, and both-empty reads as Synced.
    const bulkRows = useMemo(() => visibleRows.filter(row => row.can_calculate && !row.show_in_archive), [visibleRows]);

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            setSnapshot(await service.getYfAuditStatus());
            setLoadError(null);
        } catch (e) {
            setLoadError((e && e.error) || translate(yfTranslationKey('LOAD_ERROR')));
        } finally {
            setIsLoading(false);
        }
    }, [service, translate]);

    useEffect(() => {
        load();
    }, [load]);

    const calculate = useCallback(
        async (uanId, force = false) => {
            setStartedAt(current => ({ ...current, [uanId]: Date.now() }));
            setCalculationErrors(current => _withoutKey(current, uanId));
            try {
                const row = await service.calculateYfAuditConnector(uanId, force);
                setSnapshot(current => _withRow(current, row));
            } catch (e) {
                const message = (e && e.error) || translate(yfTranslationKey('CALCULATE_ERROR'));
                setCalculationErrors(current => ({ ...current, [uanId]: message }));
            } finally {
                setStartedAt(current => _withoutKey(current, uanId));
            }
        },
        [service, translate]
    );

    const calculateAll = useCallback(async () => {
        const pending = bulkRows.map(row => row.uan_id);
        if (!pending.length) {
            return;
        }

        stopRequested.current = false;
        setBulk({ total: pending.length, done: 0, startedAt: Date.now() });
        setQueuedUanIds(pending.slice());

        const worker = async () => {
            while (pending.length && !stopRequested.current) {
                const uanId = pending.shift();
                setQueuedUanIds(current => current.filter(id => id !== uanId));
                await calculate(uanId);
                setBulk(current => (current ? { ...current, done: current.done + 1 } : current));
            }
        };

        await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT_CALCULATIONS, pending.length) }, worker));

        setQueuedUanIds([]);
        setBulk(null);
    }, [bulkRows, calculate]);

    /** Drains the queue; requests already in flight are left to finish. */
    const stopCalculateAll = useCallback(() => {
        stopRequested.current = true;
        setQueuedUanIds([]);
    }, []);

    return {
        snapshot,
        visibleRows,
        archivedCount,
        calculableCount: bulkRows.length,
        loadError,
        calculationErrors,
        isLoading,
        startedAt,
        queuedUanIds,
        bulk,
        isCalculatingAny: Object.keys(startedAt).length > 0,
        showArchived,
        setShowArchived,
        calculate,
        calculateAll,
        stopCalculateAll,
    };
}
